/**
 * layoutGroups.ts — Pass 2 (DECORATION) for the v0.2 layout pipeline.
 *
 * SINGLE for-loop partition over input nodes; positions / creates
 * chrome relative to content positions already laid down by Pass 1
 * (the walker in topologyWalk.ts).
 *
 * Four sub-passes (per plan-triad P4 — full code, not "copy from
 * chatDagLayout.ts:X-Y"):
 *   - positionArtifactTiles: existing artifact_tile nodes get placed
 *     right of their parent turn-marker via edge resolution (per
 *     feedback_layout_follows_edges — never substitute artifact.source_turn_id raw)
 *   - positionOrphanTrays: existing orphan_tray nodes sit left of root turn
 *   - createPhaseStackGroups: NEW phase_stack_group nodes are pushed
 *     for each turn whose members were laid out by Pass 1 (one
 *     per turnKey, AABB of members + padding)
 *   - createTurnSeparators: NEW turn_separator nodes between adjacent
 *     user markers
 *
 * Returns `turnBoundingBoxes` per plan-triad P1 — `Map<turnKey, BBox>`
 * so layoutDag can expose in `LayoutResult`. Pass 2 reads content
 * positions only; never writes positions on SPINE/LATERAL/ANCHORS
 * nodes (architect invariant).
 */
import type { Node, Edge } from 'reactflow';

import { NODE_GROUP, type ChatDagNodeType, type GroupingDim } from '../types';
import { clusterBy, placeColumns, computeSpineRightEdge } from './clustering';
import { stampSpineContext } from './stampSpineContext';

// SP-2 — stable empty-set default so decorationPass callers that don't thread
// the disclosure set don't churn identity (mirrors EMPTY_COLLAPSED in context).
const EMPTY_EXPANDED_CARD_SPINES: ReadonlySet<string> = new Set();
import { CONSTANTS } from './layoutFans';
import { BLUEPRINT_NODE_SIZE } from '../chatDagTokens';

export type BBox = { x: number; y: number; w: number; h: number };

// ─── Local layout constants (mirror chatDagLayout.ts pinned values) ───

const PHASE_STACK_PADDING_X = 36;
const PHASE_STACK_PADDING_Y = 26;
const TILE_X_GAP = 24;
const TILE_VERTICAL_GAP = 16;
const TRAY_X_OFFSET = -260;

// Canonical compact-default dimensions (mirrors chatDagLayout.ts
// COMPACT_DEFAULT — the source of truth here is COMPACT_DEFAULT
// in chatDagLayout.ts; we duplicate values to avoid importing the
// pre-refactor file that gets deleted at Phase 4. Numbers match
// the baseline SHA.).
const DIM_W: Partial<Record<ChatDagNodeType, number>> = {
  turn_marker: 260,
  llm_round: 220,
  tool_call: 280,
  peer: 240,
  fan_collapsed_chip: 220,
  collapsed_phase_stack: 320,
  turn_separator: 540,
  artifact_tile: 320,
  orphan_tray: 420,
  spine_root_marker: 180,
  peer_response: 280,
  spine_fan_chip: 240,
  cycle_error: 240,
  trail_head_marker: 200,
  artifactory_blueprint: BLUEPRINT_NODE_SIZE.w,
  spine_chat_panel: 340,  // SP-3a — composite intent-chat panel
};
const DIM_H: Partial<Record<ChatDagNodeType, number>> = {
  turn_marker: 120,
  llm_round: 95,
  tool_call: 90,
  peer: 140,
  fan_collapsed_chip: 70,
  collapsed_phase_stack: 90,
  turn_separator: 24,
  artifact_tile: 112,  // SP-3b — bumped from 80 to reserve the Ask footer-band row
  orphan_tray: 110,
  spine_root_marker: 68,  // SP3a — name + task + arrival subline + target chip (4 lines); was 36
  peer_response: 120,
  spine_fan_chip: 56,
  cycle_error: 48,
  trail_head_marker: 56,
  artifactory_blueprint: BLUEPRINT_NODE_SIZE.h,
  spine_chat_panel: 420,  // SP-3a — composite intent-chat panel
};

function nodeDims(n: Node): { w: number; h: number } {
  const explicitW = (n as any).width as number | undefined;
  const explicitH = (n as any).height as number | undefined;
  const kind = n.type as ChatDagNodeType | undefined;
  return {
    w: explicitW ?? (kind ? DIM_W[kind] ?? 200 : 200),
    h: explicitH ?? (kind ? DIM_H[kind] ?? 100 : 100),
  };
}

// ─── Pass 2 entry: SINGLE for-loop partition ───

export function decorationPass(
  nodes: Node[],
  edges: Edge[],
  activeDim?: GroupingDim,
  expandedCardSpineIds: ReadonlySet<string> = EMPTY_EXPANDED_CARD_SPINES,
): Map<string, BBox> {
  const artifactTiles: Node[] = [];
  const orphanTrays: Node[] = [];

  for (const n of nodes) {
    const kind = n.type as ChatDagNodeType | undefined;
    if (!kind || NODE_GROUP[kind] !== 'DECORATION') continue;
    switch (kind) {
      case 'artifact_tile': artifactTiles.push(n); break;
      case 'orphan_tray':   orphanTrays.push(n); break;
      // phase_stack_group + turn_separator are CREATED in this pass
      // (not pre-existing in `nodes`); cycle_error already positioned
      // by walker bailout.
      default: break;
    }
  }

  // SP-3b — ids of tiles that have a live artifact spine root. SHARED by the
  // cluster-exclusion (below) and positionIntentSpine (B4) so a tile is never
  // both clustered AND laned. Keyed on the MARKER, NOT on expansion state — a
  // COLLAPSED spined tile is still laned (with a stem), so it too must be
  // excluded from clustering.
  const spinedTileIds = new Set<string>(
    nodes
      .filter((n) => n.type === 'spine_root_marker'
        && (n.data as any)?.spawnSource === 'artifact'
        && (n.data as any)?.sourceArtifactId)
      .map((n) => `tile-${(n.data as any).sourceArtifactId}`),
  );

  const grouped = !!activeDim && activeDim !== 'off';
  if (artifactTiles.length) {
    if (grouped) {
      // Trailmap L2 Drip 2 — relocate tiles into L→R clusters past the spine,
      // and create a cluster_aabb behind each (unshift = paint-behind, like
      // phase_stack_group). phase_stack boxes STAY (L2 parity, §3.4); tiles
      // carry no turnKey so they're never phase-stack members → no distortion.
      // SP-3b — a spined tile is promoted out of clustering into its own lane
      // by positionIntentSpine (B4); keep it out of clusterBy/placeColumns so
      // it isn't double-placed in a column.
      const toCluster = artifactTiles.filter((t) => !spinedTileIds.has(t.id));
      const spineRightEdge = computeSpineRightEdge(nodes);
      const clusters = toCluster.length
        ? placeColumns(clusterBy(toCluster, activeDim!), activeDim!, spineRightEdge)
        : [];
      const aabbs: Node[] = clusters.map((c) => ({
        id: `cluster-${activeDim}-${c.value ?? 'null'}`,
        type: 'cluster_aabb',
        position: { x: c.bbox.x, y: c.bbox.y },
        data: {
          kind: 'cluster_aabb',
          dim: c.dim, value: c.value, count: c.count, bbox: c.bbox,
          soloOfDim: clusters.length === 1,
        },
        width: c.bbox.w,
        height: c.bbox.h,
        style: { width: c.bbox.w, height: c.bbox.h },
        selectable: false,
        draggable: false,
        focusable: false,
      } as Node));
      if (aabbs.length) nodes.unshift(...aabbs);
    } else {
      positionArtifactTiles(artifactTiles, nodes, edges);
    }
  }
  if (orphanTrays.length)   positionOrphanTrays(orphanTrays, nodes);

  // Trail head marker — synthetic chrome chip pinned ABOVE the topmost
  // node of the trail. Identifies the spine root regardless of role
  // (user / orphan-asst / spine_root_marker). Operator request from
  // TS-03 screenshot review: 7-orphan-asst seed pushed the user_marker
  // down, leaving the trail's visual top "rooted" by a bare round —
  // ambiguous. The marker pins the start of the trail unambiguously.
  const newTrailHead = createTrailHeadMarker(nodes);
  if (newTrailHead) {
    nodes.unshift(newTrailHead);
    // Convene-standby: companions are authored as ROOTS (no incoming edge), so
    // fan every parentless spine-root from the trail head — siblings setting out,
    // not floaters. Snapshot parents BEFORE pushing so all roots get an edge.
    const parented = new Set(edges.map((e) => e.target));
    for (const n of nodes) {
      // SP2a/SP-3b — a source-card spine root (blueprint OR artifact) is edged
      // to its card/tile (not the trail head); skip it explicitly (robust even
      // if the synthetic edge hasn't landed in `parented` yet).
      if (n.type !== 'spine_root_marker' || parented.has(n.id)
          || (n.data as any)?.spawnSource === 'blueprint_intent'
          || (n.data as any)?.spawnSource === 'artifact') continue;
      edges.push({
        id: `e-trailhead-${n.id}`,
        source: 'trail-head-marker',
        target: n.id,
        sourceHandle: 'b',
        targetHandle: 't',
        type: 'fork',
      } as Edge);
    }
  }

  // Living Blueprint SP1 — pin the summoned blueprint card to the trail's
  // upper-left, just left of the spine root. Mutates the emitted node's
  // position (it arrives at origin from the processor).
  positionBlueprintNode(nodes);
  // Source Spines SP-2 — stamp spineSpawnSource on the blueprint spine's
  // peer_response descendants (discriminates them from squad peer_responses,
  // which share the kind). Must run BEFORE positionIntentSpine so the same
  // subtree walk sees a consistent node set; idempotent across recomputes.
  stampSpineContext(nodes, edges);
  // SP2a — pin the blueprint's intent spine directly BELOW the card (the card
  // roots it). Must run AFTER positionBlueprintNode (needs the card's final pos)
  // and BEFORE group/bbox creation so the stacks frame the moved spine. SP-2 —
  // also collapses the spine (hide + stem) when the card isn't expanded.
  positionIntentSpine(nodes, edges, expandedCardSpineIds);

  const turnBoundingBoxes = new Map<string, BBox>();
  const newGroups = createPhaseStackGroups(nodes, turnBoundingBoxes);
  // PREPEND phase_stack_groups (NOT append) — ReactFlow paints array
  // order, later = on top. Groups must paint BEHIND content so the
  // round/tool/peer cards inside aren't obscured by the group's
  // semi-transparent fill + border. Matches the OLD chatDagLayout's
  // `return [...groups, ...nodes]` pattern.
  if (newGroups.length) nodes.unshift(...newGroups);
  // Separators are thin chrome ON TOP of the spine gap — they should
  // paint last (push). Hidden behind groups would defeat the purpose.
  const newSeparators = createTurnSeparators(nodes);
  for (const s of newSeparators) nodes.push(s);

  return turnBoundingBoxes;
}

// ─── Blueprint node (SP1 — flip card pinned to the trail upper-left) ───

const BLUEPRINT_GAP_X = 64; // horizontal gap between card right edge and spine root

// Find the topmost SPINE/ANCHORS node (same exclusion rationale as the trail
// head marker scan — LATERAL/DECORATION can sit above the logical root).
function _topmostSpineNode(allNodes: Node[]): Node | undefined {
  let topNode: Node | undefined;
  let topY = Infinity;
  let topAbsX = Infinity;
  for (const n of allNodes) {
    const kind = n.type as ChatDagNodeType | undefined;
    if (!kind) continue;
    const group = NODE_GROUP[kind];
    if (group !== 'SPINE' && group !== 'ANCHORS') continue;
    const y = n.position?.y ?? 0;
    const absX = Math.abs(n.position?.x ?? 0);
    if (y < topY || (y === topY && absX < topAbsX)) {
      topY = y;
      topAbsX = absX;
      topNode = n;
    }
  }
  return topNode && Number.isFinite(topY) ? topNode : undefined;
}

function positionBlueprintNode(allNodes: Node[]): void {
  const bp = allNodes.find((n) => n.type === 'artifactory_blueprint');
  if (!bp) return;
  const root = _topmostSpineNode(allNodes);
  if (!root) return; // no spine yet (empty trail) — leave at origin
  const rootX = root.position?.x ?? 0;
  const rootY = root.position?.y ?? 0;
  bp.position = { x: rootX - DIM_W.artifactory_blueprint! - BLUEPRINT_GAP_X, y: rootY };
}

const INTENT_SPINE_GAP_Y = 48; // gap between card bottom and the intent-spine root top
const STEM_H = 48;             // SP-2 — height of the dormant stem under a collapsed card
const PANEL_GAP_Y = 12;        // SP-3a — gap between the root lane-head band and the panel top

// SP2a — relocate the blueprint intent spine (root + its seeded Foreman peer
// chain) to hang directly below the card. The main walk positions the root as a
// LEFT-lane camp and its children at ABSOLUTE coords relative to that lane, so we
// shift the WHOLE subtree by a single delta — moving the root alone would orphan
// its seeded responses.
// SP-2 — extended for collapse. The card roots its intent spine: when the card
// is in expandedCardSpineIds the subtree shifts below it, un-hides, and each
// reply gets a laneIndex (cascade stagger). When collapsed, the replies + their
// edges are hidden and a short dormant stem is pushed below the card (the root
// marker stays visible as the card's lane-head band). Void + mutates in place.
// No lane cap (rev2 fold) — long lanes are bounded by per-bubble scroll.
const INTENT_LANE_GAP_X = 64;     // gap between promoted lanes (and before the first)
const INTENT_LANE_BAND_Y = 80;    // top of the promoted-lane band

// SP-3b — lanes start to the RIGHT of the spine and the widest clustered AABB,
// computed from live geometry — never a hardcoded x (feedback_layout_follows_edges).
function computeLaneBandOriginX(nodes: Node[]): number {
  let right = computeSpineRightEdge(nodes);
  for (const n of nodes) {
    if (n.type === 'cluster_aabb') {
      right = Math.max(right, (n.position?.x ?? 0) + (((n as any).width as number | undefined) ?? 0));
    }
  }
  return right + INTENT_LANE_GAP_X;
}

// SP-3b — generalized from the single blueprint card to N source-cards. Source-
// cards = the blueprint card (pinned by positionBlueprintNode) + every artifact
// tile that has a live spine root. Each gets a lane; the shared per-card work
// (subtree shift + collapse/expand + panel placement) is promoteCardLane().
export function positionIntentSpine(
  allNodes: Node[],
  edges: Edge[],
  expandedCardSpineIds: ReadonlySet<string>,
): void {
  const byId = new Map(allNodes.map((n) => [n.id, n]));
  const childrenByParent = new Map<string, string[]>();
  for (const e of edges) {
    if (!childrenByParent.has(e.source)) childrenByParent.set(e.source, []);
    childrenByParent.get(e.source)!.push(e.target);
  }
  type SourceCard = { card: Node; root: Node; laneTurnY: number };
  const cards: SourceCard[] = [];
  const bp = allNodes.find((n) => n.type === 'artifactory_blueprint');
  if (bp) {
    const root = allNodes.find((n) => n.type === 'spine_root_marker'
      && (n.data as any)?.spawnSource === 'blueprint_intent');
    if (root) cards.push({ card: bp, root, laneTurnY: bp.position?.y ?? 0 });
  }
  for (const n of allNodes) {
    if (n.type !== 'artifact_tile') continue;
    const root = allNodes.find((r) => r.type === 'spine_root_marker'
      && (r.data as any)?.spawnSource === 'artifact'
      && `tile-${(r.data as any)?.sourceArtifactId}` === n.id);
    if (root) cards.push({ card: n, root, laneTurnY: n.position?.y ?? 0 });
  }
  if (!cards.length) return;
  // Lane order by producing-turn Y (timeline). Blueprint keeps its pinned x.
  cards.sort((a, b) => a.laneTurnY - b.laneTurnY);
  let cursorX = computeLaneBandOriginX(allNodes);
  for (const c of cards) {
    const isBlueprint = c.card.type === 'artifactory_blueprint';
    if (!isBlueprint) {
      c.card.position = { x: cursorX, y: INTENT_LANE_BAND_Y };
    }
    promoteCardLane(c.card, c.root, allNodes, edges, byId, childrenByParent, expandedCardSpineIds);
    if (!isBlueprint) {
      // Advance past THIS lane's ACTUAL footprint — card OR its visible panel,
      // whichever is wider — so a content-expanded tile can't overlap its
      // neighbor. Blueprint keeps its own pinned x, no cursor move.
      const cardW = ((c.card as any).width as number | undefined) ?? 320;
      const panel = allNodes.find((n) => n.type === 'spine_chat_panel'
        && (n.data as any)?.cardId === c.card.id);
      const panelW = panel && !panel.hidden ? (((panel as any).width as number | undefined) ?? 340) : 0;
      cursorX += Math.max(cardW, panelW) + INTENT_LANE_GAP_X;
    }
  }
}

// SP-3b — the shared per-card lane work (extracted from the old single-card
// positionIntentSpine body). Reads the card's CURRENT position (the caller pins
// artifact cards to a lane slot first; the blueprint keeps positionBlueprintNode's
// pin). byId + childrenByParent are caller-built once; seen/subtree/stack stay
// local (per-lane). Mutates allNodes/edges in place.
function promoteCardLane(
  card: Node,
  root: Node,
  allNodes: Node[],
  edges: Edge[],
  byId: Map<string, Node>,
  childrenByParent: Map<string, string[]>,
  expandedCardSpineIds: ReadonlySet<string>,
): void {
  const expanded = expandedCardSpineIds.has(card.id);
  // SP-3c — stamp the spine-lane collapse state onto the source-card node so the
  // tile renderer drives its own ⌄/▸ Spine chevron. promoteCardLane is the ONLY
  // writer and runs ONLY for cards with a resolved spine root, so presence of
  // this value IS "has a spine" (a spine-less tile stays undefined → renders
  // "Ask"). NOT data.expanded (that's content-viewer state — name-trap).
  // Harmless on the blueprint card (BlueprintNode reads runtime directly).
  (card.data as any).spineExpanded = expanded;

  const cardH = ((card as any).height as number | undefined) ?? DIM_H.artifactory_blueprint!;
  const targetX = card.position?.x ?? 0;
  const targetY = (card.position?.y ?? 0) + cardH + INTENT_SPINE_GAP_Y;
  const dx = targetX - (root.position?.x ?? 0);
  const dy = targetY - (root.position?.y ?? 0);

  // Collect the subtree (root + all descendants) via the edge graph (byId +
  // childrenByParent are caller-built). `seen` includes the root; `subtree` is
  // descendants only, in arrival order.
  const seen = new Set<string>();
  const subtree: string[] = [];
  const stack = [root.id];
  while (stack.length) {
    const id = stack.pop()!;
    if (seen.has(id)) continue;
    seen.add(id);
    const n = byId.get(id);
    if (n) {
      if (dx || dy) n.position = { x: (n.position?.x ?? 0) + dx, y: (n.position?.y ?? 0) + dy };
      if (id !== root.id) subtree.push(id);
    }
    for (const c of childrenByParent.get(id) ?? []) if (!seen.has(c)) stack.push(c);
  }
  // SP-3a — the per-reply cascade is replaced by one spine_chat_panel emitted
  // by the processor (zero canvas edges, identified by cardId). The panel hangs
  // BELOW the root lane-head band; the band still reads as the card's bottom.
  const rootH = ((root as any).height as number | undefined) ?? DIM_H.spine_root_marker!;
  const panel = allNodes.find(
    (n) => n.type === 'spine_chat_panel' && (n.data as any)?.cardId === card.id,
  );

  if (!expanded) {
    // COLLAPSED — hide any legacy subtree (peer_response cascades on pre-SP-3a
    // sessions) + their edges, hide the panel, drop a dormant stem. The root
    // marker stays visible (the card's lane-head band).
    for (const id of subtree) { const n = byId.get(id); if (n) n.hidden = true; }
    for (const e of edges) { if (seen.has(e.source) && seen.has(e.target)) e.hidden = true; }
    if (panel) panel.hidden = true;
    allNodes.push({
      id: `stem-${card.id}`,
      type: 'spine_lane_stem',
      position: { x: targetX, y: (card.position?.y ?? 0) + cardH },
      // SP-3c — widened from 1 to a real click target (stem is now click-to-
      // re-expand). Rail stays visually thin via items-center on this footprint.
      width: 16,
      height: STEM_H,
      draggable: false,
      selectable: false,
      data: { kind: 'spine_lane_stem', cardId: card.id },
    } as Node);
    return;
  }

  // EXPANDED — un-hide any legacy subtree + edges; place the panel below the
  // root band, aligned to the card x. No stem, no fitView (verified: fitView
  // keys on inFlightTurnId/bbox-bucket/messages.length, not expansion state).
  for (const id of subtree) { const n = byId.get(id); if (n) n.hidden = false; }
  for (const e of edges) { if (seen.has(e.source) && seen.has(e.target)) e.hidden = false; }
  if (panel) {
    panel.hidden = false;
    panel.position = { x: targetX, y: (root.position?.y ?? targetY) + rootH + PANEL_GAP_Y };
  }
}

// ─── Trail head marker (v0.2.x — chrome chip above spine root) ───

const TRAIL_HEAD_GAP_Y = 28; // vertical distance from marker bottom to root top

function createTrailHeadMarker(allNodes: Node[]): Node | null {
  // Find the topmost SPINE or ANCHORS node (lowest y). LATERAL nodes
  // (peers, tool_calls, fan_collapsed_chip) are deliberately excluded
  // even though they have positions — peers in a consult_peers polar
  // arc can have STRONGLY NEGATIVE y values (above the spine root)
  // because the fan sweeps the upper half-plane around the parent
  // tool's center. Without this filter, the trail head pinned above
  // peer1 instead of above the actual spine root (user_marker / round /
  // spine_root_marker) — operator saw the marker drift to top-right
  // of the canvas, disconnected from the trail's logical start.
  //
  // DECORATION nodes also excluded (phase_stack_groups, separators,
  // the marker itself, cycle_error).
  // Anchor over the MAIN spine (x≈0), not a side-lane camp/companion: pick the
  // topmost SPINE/ANCHORS node, tie-breaking by smallest |x| so a standby camp
  // riding a ±CAMP_LANE_DX lane at the same top y can't steal the trail head
  // (the camp is the data-root msg, so it sorts first in the node array).
  let topNode: Node | undefined;
  let topY = Infinity;
  let topAbsX = Infinity;
  for (const n of allNodes) {
    const kind = n.type as ChatDagNodeType | undefined;
    if (!kind) continue;
    const group = NODE_GROUP[kind];
    if (group !== 'SPINE' && group !== 'ANCHORS') continue;
    const y = n.position?.y ?? 0;
    const absX = Math.abs(n.position?.x ?? 0);
    if (y < topY || (y === topY && absX < topAbsX)) {
      topY = y;
      topAbsX = absX;
      topNode = n;
    }
  }
  if (!topNode || !Number.isFinite(topY)) return null;

  const rootX = topNode.position?.x ?? 0;
  const rootKind = topNode.type as ChatDagNodeType;
  const markerW = DIM_W.trail_head_marker!;
  const markerH = DIM_H.trail_head_marker!;
  // Center the marker horizontally on the root; sit just above with a
  // small gap so the visual reads "this is the start, content is below".
  const rootW =
    ((topNode as any).width as number | undefined) ?? (DIM_W[rootKind] ?? 200);
  const markerX = rootX + (rootW - markerW) / 2;
  const markerY = topY - markerH - TRAIL_HEAD_GAP_Y;

  return {
    id: 'trail-head-marker',
    type: 'trail_head_marker',
    position: { x: markerX, y: markerY },
    data: {
      kind: 'trail_head_marker',
      rootNodeId: topNode.id,
      rootNodeKind: rootKind,
    },
    width: markerW,
    height: markerH,
    style: { width: markerW, height: markerH },
    selectable: false,
    draggable: false,
  } as Node;
}

// ─── 3.3b: createPhaseStackGroups (lifted from chatDagLayout.ts:781-897) ───

function createPhaseStackGroups(
  allNodes: Node[],
  bboxOut: Map<string, BBox>,
): Node[] {
  const collapsedTurnKeys = new Set<string>();
  for (const n of allNodes) {
    if (n.type === 'collapsed_phase_stack') {
      const key = (n.data as any)?.turnKey as string | undefined;
      if (key) collapsedTurnKeys.add(key);
    }
  }

  const membersByTurnKey = new Map<string, Node[]>();
  const userMarkerByTurnKey = new Map<string, Node>();
  // Asst-marker fallback per turnKey — used when there's no user_marker
  // for the turn (orphan-asst messages like a persona's seed content).
  // Without this fallback, T4.3's phantom-user-marker deletion silently
  // suppressed phase_stack_group chrome for those turns; canopy lens
  // showed bare nodes with no enclosing chrome, reading as "no root".
  const asstMarkerByTurnKey = new Map<string, Node>();
  for (const n of allNodes) {
    const key = (n.data as any)?.turnKey as string | undefined;
    if (!key) continue;
    if (collapsedTurnKeys.has(key)) continue;
    if (n.type === 'turn_marker') {
      const role = (n.data as any)?.role;
      if (role === 'user') userMarkerByTurnKey.set(key, n);
      else if (role === 'assistant') asstMarkerByTurnKey.set(key, n);
      continue;
    }
    if (!membersByTurnKey.has(key)) membersByTurnKey.set(key, []);
    membersByTurnKey.get(key)!.push(n);
  }

  const groups: Node[] = [];
  membersByTurnKey.forEach((members, turnKey) => {
    // Prefer user_marker as the turn's identity anchor; fall back to
    // asst_marker for orphan-asst turns (persona seed content, peer-
    // spine extensions, council-approved forks).
    const anchorMarker =
      userMarkerByTurnKey.get(turnKey) ?? asstMarkerByTurnKey.get(turnKey);
    if (!anchorMarker) return;
    const turnIndex = (anchorMarker.data as any).turnIndex ?? 0;

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    let roundCount = 0;
    let toolCount = 0;
    for (const n of members) {
      if (n.type === 'llm_round') roundCount += 1;
      if (n.type === 'tool_call') toolCount += 1;
      const { w, h } = nodeDims(n);
      minX = Math.min(minX, n.position.x);
      minY = Math.min(minY, n.position.y);
      maxX = Math.max(maxX, n.position.x + w);
      maxY = Math.max(maxY, n.position.y + h);
    }
    if (!Number.isFinite(minX)) return;

    const previewRaw = ((anchorMarker.data as any)?.preview ?? '') as string;
    const preview = previewRaw.replace(/^[\s>#*_`+\-]+\s*/, '').trim() || previewRaw;
    const name = preview.length > 48 ? preview.slice(0, 47) + '…' : preview;

    const width = maxX - minX + 2 * PHASE_STACK_PADDING_X;
    const height = maxY - minY + 2 * PHASE_STACK_PADDING_Y;
    const x = minX - PHASE_STACK_PADDING_X;
    const y = minY - PHASE_STACK_PADDING_Y;

    bboxOut.set(turnKey, { x, y, w: width, h: height });

    groups.push({
      id: `group-stack-${turnKey}`,
      type: 'phase_stack_group',
      position: { x, y },
      data: {
        kind: 'phase_stack_group',
        turnKey,
        turnIndex,
        name: name || '(empty prompt)',
        roundCount,
        toolCount,
        childCount: members.length,
      },
      width,
      height,
      style: { width, height },
      selectable: false,
      draggable: false,
      focusable: false,
    } as Node);
  });

  return groups;
}

// ─── 3.3c: createTurnSeparators (lifted from chatDagLayout.ts:722-779) ───

function createTurnSeparators(allNodes: Node[]): Node[] {
  const userMarkers = allNodes
    .filter((n) => n.type === 'turn_marker' && (n.data as any)?.role === 'user')
    .sort(
      (a, b) =>
        ((a.data as any)?.turnIndex ?? 0) - ((b.data as any)?.turnIndex ?? 0),
    );
  if (userMarkers.length < 2) return [];

  const sepW = DIM_W.turn_separator!;
  const sepH = DIM_H.turn_separator!;
  const out: Node[] = [];

  for (let i = 1; i < userMarkers.length; i++) {
    const userNext = userMarkers[i];
    const turnIdx = ((userNext.data as any).turnIndex ?? i) as number;
    const ts = (userNext.data as any).timestamp as string | undefined;
    let label = `Turn ${turnIdx + 1}`;
    if (ts) {
      try {
        const stamp = new Date(ts).toLocaleTimeString([], {
          hour: '2-digit', minute: '2-digit',
        });
        label = `${label} · ${stamp}`;
      } catch {
        // fall back to bare label
      }
    }
    const sepY =
      userNext.position.y - CONSTANTS.TURN_VERTICAL_GAP / 2 - sepH / 2;
    const userW = nodeDims(userNext).w;
    const sepX = userNext.position.x + userW / 2 - sepW / 2;
    out.push({
      id: `sep-turn-${turnIdx}`,
      type: 'turn_separator',
      position: { x: sepX, y: sepY },
      data: {
        kind: 'turn_separator',
        turnIndex: turnIdx,
        label,
        timestamp: ts,
      },
      width: sepW,
      height: sepH,
      draggable: false,
      selectable: false,
    } as Node);
  }
  return out;
}

// ─── 3.3d: positionArtifactTiles (extracted from chatDagLayout.ts:601-712) ───

function positionArtifactTiles(
  tiles: Node[],
  allNodes: Node[],
  edges: Edge[],
): void {
  // Build (tile_id -> parent_node_id) from edges so layout follows the
  // data processor's intent — never substitute artifact.source_turn_id
  // (per feedback_layout_follows_edges).
  const tileParentNodeId = new Map<string, string>();
  for (const e of edges) {
    if (typeof e.id === 'string' && e.id.startsWith('edge-tile-')) {
      tileParentNodeId.set(e.target, e.source);
    }
  }

  const nodeById = new Map<string, Node>();
  for (const n of allNodes) nodeById.set(n.id, n);

  // Defensive fallback: lookup by data.messageId match for test
  // fixtures with synthesized tiles + no edges.
  const turnByMsgId = new Map<string, Node>();
  for (const n of allNodes) {
    if (n.type === 'turn_marker') {
      const mid = (n.data as any)?.messageId;
      if (mid) turnByMsgId.set(mid, n);
    }
  }

  const tilesByParent = new Map<string, Node[]>();
  for (const tile of tiles) {
    const parentNodeId = tileParentNodeId.get(tile.id);
    let parent: Node | undefined = parentNodeId ? nodeById.get(parentNodeId) : undefined;
    if (!parent) {
      const parentMsg = (tile.data as any)?.artifact?.source_turn_id;
      parent = parentMsg ? turnByMsgId.get(parentMsg) : undefined;
    }
    if (!parent) continue;
    if (!tilesByParent.has(parent.id)) tilesByParent.set(parent.id, []);
    tilesByParent.get(parent.id)!.push(tile);
  }

  const tileH = DIM_H.artifact_tile!;
  for (const [parentId, tilesForParent] of tilesByParent) {
    const parent = nodeById.get(parentId);
    if (!parent) continue;
    const { w: parentW } = nodeDims(parent);
    const tileX = parent.position.x + parentW + TILE_X_GAP;
    tilesForParent.forEach((tile, i) => {
      tile.position = {
        x: tileX,
        y: parent.position.y + i * (tileH + TILE_VERTICAL_GAP),
      };
    });
  }
}

// ─── 3.3d cont.: positionOrphanTrays (extracted from chatDagLayout.ts:713-720) ───

function positionOrphanTrays(trays: Node[], allNodes: Node[]): void {
  let rootTurn: Node | undefined;
  for (const n of allNodes) {
    if (n.type === 'turn_marker') {
      rootTurn = n;
      break;
    }
  }
  if (!rootTurn) return;
  for (const tray of trays) {
    tray.position = {
      x: rootTurn.position.x + TRAY_X_OFFSET,
      y: rootTurn.position.y,
    };
  }
}
